import { memo, type ReactNode, useCallback, useState } from 'react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import styled from 'styled-components';

import OrganizationAvatar, {
  OrganizationAvatarWrapper,
} from './OrganizationAvatar';
import { SidebarTooltip } from './SidebarTooltip';

import type { Showcase } from '@src/types.ts/story';

type OrganizationAvatarWithTooltipProps = {
  album?: Showcase | null;
  username?: string | null;
  logoSize?: number;
  compact?: boolean;
  hoverLabel?: string;
  email?: string;
  renderReactTooltip: () => ReactNode;
};

function OrganizationAvatarWithTooltip({
  album,
  username,
  logoSize = 34,
  compact = false,
  hoverLabel,
  email,
  renderReactTooltip,
}: OrganizationAvatarWithTooltipProps) {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const handleAfterShow = useCallback(() => setIsPopupOpen(true), []);
  const handleAfterHide = useCallback(() => setIsPopupOpen(false), []);

  if (!album) {
    return null;
  }
  return (
    <div data-popup-open={isPopupOpen || undefined}>
      <AvatarWrapper
        data-avatar-wrapper
        data-tooltip-id="org-avatar-tooltip"
        data-tooltip-content="-"
      >
        <OrganizationAvatar
          album={album}
          username={username}
          logoSize={logoSize}
          compact={compact}
          email={email}
        />
        {compact && hoverLabel && <HoverTooltip>{hoverLabel}</HoverTooltip>}
      </AvatarWrapper>
      <TooltipWrapper>
        <ReactTooltip
          id="org-avatar-tooltip"
          render={renderReactTooltip}
          style={{
            background: 'none',
            pointerEvents: 'all',
            zIndex: 1000020,
            transform: 'translateX(4px)',
          }}
          place="bottom"
          offset={0}
          openEvents={{
            mouseenter: false,
            click: true,
          }}
          closeEvents={{
            mouseleave: false,
            click: true,
          }}
          afterShow={handleAfterShow}
          afterHide={handleAfterHide}
        />
      </TooltipWrapper>
    </div>
  );
}

export default memo(OrganizationAvatarWithTooltip);

const HoverTooltip = styled(SidebarTooltip)``;

const AvatarWrapper = styled(OrganizationAvatarWrapper)`
  position: relative;
  align-items: center;
  gap: 12px;
  user-select: none;
  cursor: pointer;

  &:hover ${HoverTooltip} {
    opacity: 1;
  }

  /* Hide the hover tooltip when the click popover is open */
  [data-popup-open] & ${HoverTooltip} {
    opacity: 0;
  }
`;

const TooltipWrapper = styled.div`
  --rt-opacity: 1;
  --rt-z-index: 1000020;
`;
